import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import styled from "styled-components";
import remarkGfm from "remark-gfm";
import { Spacer, colors } from "@qivia/ui";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import { defaultSchema } from "rehype-sanitize";
import { Schema } from "rehype-sanitize/lib";

const FAQ_URL = "FAQ_drivers.md";

const baseTagNames = (defaultSchema as { tagNames?: string[] }).tagNames || [];
const baseAttributes =
  (defaultSchema as { attributes?: Record<string, unknown[]> }).attributes ||
  {};
const baseDetailsAttrs = Array.isArray(baseAttributes.details)
  ? baseAttributes.details
  : [];
const baseSummaryAttrs = Array.isArray(baseAttributes.summary)
  ? baseAttributes.summary
  : [];

const faqSchema = {
  ...defaultSchema,
  tagNames: [...baseTagNames, "details", "summary"],
  attributes: {
    ...baseAttributes,
    details: [...baseDetailsAttrs, ["open"], ["className"], ["class"]],
    summary: [...baseSummaryAttrs, ["className"], ["class"]],
  },
} as Schema;

const createDetailsHtml = (question: string, body: string): string => {
  const cleanedBody = body.trim();
  // Escape HTML special characters in the question
  const escapedQuestion = question
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

  return `<details class="faq-item">
    <summary class="faq-title">${escapedQuestion}</summary>
  
  ${cleanedBody}
  
  </details>`;
};

const transformNotionFaqToDetails = (md: string): string => {
  if (!md) return "";

  const lines = md.replace(/\r\n/g, "\n").split("\n");
  const parts: string[] = [];
  let currentQuestion: string | null = null;
  let currentAnswer: string[] = [];

  const processQuestion = () => {
    if (currentQuestion && currentAnswer.length > 0) {
      const answer = currentAnswer
        .join("\n")
        .replace(/\n---\s*$/, "")
        .trim();
      parts.push(createDetailsHtml(currentQuestion, answer));
      currentQuestion = null;
      currentAnswer = [];
    }
  };

  for (const line of lines) {
    const trimmed = line.trim();

    // Detect a section title (T :)
    if (trimmed.match(/^#+\s+\*\*T\s*:\s*.+\*\*$/)) {
      processQuestion();
      // Remove the "T :" prefix from the title
      const cleanedTitle = line.replace(/\*\*T\s*:\s*/, "**");
      parts.push(cleanedTitle);
      continue;
    }

    // Detect a question (Q :)
    const questionMatch = trimmed.match(/^###\s+\*\*Q\s*:\s*(.+?)\*\*$/);
    if (questionMatch) {
      processQuestion();
      currentQuestion = questionMatch[1].trim();
      continue;
    }

    // Add the line to the current answer
    if (currentQuestion !== null) {
      currentAnswer.push(line);
    } else if (trimmed) {
      // Line that is neither a question nor a section title (content before the first question)
      parts.push(line);
    }
  }

  // Process the last question
  processQuestion();

  return parts.join("\n");
};

export const FAQPage = () => {
  const [content, setContent] = useState("");
  const transformed = transformNotionFaqToDetails(content);

  useEffect(() => {
    fetch(FAQ_URL)
      .then((res) => res.text())
      .then(setContent)
      .catch(console.error);
  }, []);

  return (
    <StyledFAQContainer>
      <Spacer x={2} />
      <StyledColumn>
        <Spacer y={2} />
        <StyledMarkdownWrapper>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw, [rehypeSanitize, faqSchema]]}
          >
            {transformed}
          </ReactMarkdown>
        </StyledMarkdownWrapper>
        <Spacer y={2} />
      </StyledColumn>
      <Spacer x={2} />
    </StyledFAQContainer>
  );
};

const StyledFAQContainer = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  overflow-y: auto;
`;
const StyledColumn = styled.div`
  flex-direction: column;
  display: flex;
  width: 100%;
`;
const StyledMarkdownWrapper = styled.div`
  width: 100%;

  details.faq-item {
    margin-bottom: 1rem;
    border: 1px solid ${colors.ligthGrey};
    border-radius: 0.5rem;
    padding: 1rem;

    summary.faq-title {
      cursor: pointer;
      font-weight: 600;
      font-size: 1.125rem;
      list-style: none;
      user-select: none;
      display: flex;
      align-items: center;

      &::-webkit-details-marker,
      &::marker {
        display: none;
      }

      &::before {
        content: "▸ ";
        margin-right: 0.5rem;
        flex-shrink: 0;
      }

      &:hover {
        color: ${colors.primary};
      }
    }

    &[open] summary.faq-title {
      margin-bottom: 1rem;

      &::before {
        content: "▾ ";
      }
    }

    & > *:not(summary) {
      margin-top: 1rem;
    }

    p,
    li {
      word-break: break-word;
      overflow-wrap: break-word;
    }
  }
`;
